import { defineStore } from "pinia"
import { computed, ref } from "vue"
import axios from "axios"
import { ServiceError } from "@core/exceptions"
import {
    OptimTurpeV2Service,
    type OptimTurpeV2RunBody,
    type OptimTurpeV2RunResult,
} from "@modules/courtage/services/optim-turpe-v2.service"
import {
    optimTurpeV2MockC4,
    optimTurpeV2MockEtudes,
} from "@modules/courtage/stores/optim-turpe-v2.mock-data"
import {
    getEconomieFermeEur,
    resolveOptimTurpeV2State,
    resolveServiceErrorMessage,
    consigneApresRefus,
    estErreurTechnique,
    cmdpsApplicable,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    CODE_SCENARIO_RETENU,
    scenarioConseille,
    scenarioRetenuServi,
} from "@modules/courtage/utils/optim-turpe-v2.arbitrage"
import {
    messageEtudeSansDocument,
    refusConnuDe,
    refusSApplique,
    saisiesDuLancement,
} from "@modules/courtage/utils/optim-turpe-v2.echec"
import type { RefusConnu } from "@modules/courtage/utils/optim-turpe-v2.echec"
import { verdictSegment } from "@modules/courtage/utils/optim-turpe-v2.perimetre"
import {
    corpsAvecMargeParDefaut,
    surchargesAvecMargeParDefaut,
} from "@modules/courtage/utils/optim-turpe-v2.marge"
import {
    MESSAGE_AUCUNE_ETUDE_VUE,
    MESSAGE_CALCUL_EN_COURS,
    MESSAGE_CALCUL_PLUS_LONG,
    MESSAGE_CALCUL_SANS_SUIVI,
    messageEtudeNonTerminale,
    MESSAGE_VERIFICATION_IMPOSSIBLE,
    estEchecNomme,
    estPanneDeReponse,
    relireEtudesApresPanne,
    suivreCalculEnCours,
} from "@modules/courtage/utils/optim-turpe-v2.relecture"
import {
    CYCLE_ROUTES_ACTIVES,
    PERIMETRE_SERVI_ACTIF,
} from "@modules/courtage/constants/optim-turpe-v2"
import {
    idsRejouables,
    lancementAOuvrir,
    lancementAOuvrirALArrivee,
    lancementEnCours,
    lireInstant,
    trierParDateDeCalcul,
} from "@modules/courtage/utils/optim-turpe-v2.cycle"
import type {
    OptimTurpeV2EtatServi,
    OptimTurpeV2Etude,
    OptimTurpeV2Perimetre,
} from "@modules/courtage/types/optim-turpe-v2"

/** Scénario d'arbitrage CMDPS (Z3, DEV-783) : sans dépassement / avec dépassement assumé. */
export type OptimTurpeV2Scenario = "sans" | "avec"

/**
 * Ce que la page sait du compteur au moment de lancer : son segment, tel que la
 * source le porte. Il décide seulement du NOMBRE de scénarios (un seul sur un
 * C5, qui n'a pas de CMDPS) ; il n'est jamais envoyé au moteur.
 */
export type OptionsLancement = { segment?: string | null }

/**
 * L'étude porte-t-elle déjà son texte de conclusion ? C'est le cas de toute
 * étude calculée depuis courtage#249 (composé dans la tâche), et de toute étude
 * dont la route `explain` a déjà écrit le texte.
 */
export function explainStocke(
    etude:
        | { explain?: { conclusion?: string | null } | null }
        | null
        | undefined
): boolean {
    const conclusion = etude?.explain?.conclusion
    return typeof conclusion === "string" && conclusion.trim().length > 0
}

/**
 * Combien de temps la page RELIT une étude aboutie avant d'appeler la route
 * `explain` (DEV-2311, F2). La tâche du moteur écrit la conclusion 11 à 17 s
 * après le SUCCESS (jusqu'à 24,8 s mesurées au bêta-test du 02/10) : appeler la
 * route avant faisait tourner le modèle deux fois.
 */
export const NARRATION_ATTENTE_MS = 25_000
/** Cadence de relecture de l'étude pendant cette attente. */
export const NARRATION_INTERVALLE_MS = 5_000
/**
 * Au delà de ce délai après le calcul (`outputs.calcul.calcule_le`), la tâche
 * n'écrira plus de conclusion : une étude qui n'en porte pas l'obtient par la
 * route, sans attente.
 */
export const NARRATION_FENETRE_TACHE_MS = 120_000

/**
 * Cadence de relecture quand le moteur dit la narration « en_cours »
 * (`outputs.narration`, courtage#274) : entre 3 et 5 s.
 */
export const NARRATION_EN_COURS_INTERVALLE_MS = 4_000
/**
 * Plafond du suivi d'une narration « en_cours » : passé ce délai, la page
 * cesse de relire sans appeler la route ; la conclusion sera là à la
 * prochaine ouverture.
 */
export const NARRATION_EN_COURS_PLAFOND_MS = 120_000

/**
 * Ce que la page fait de la narration d'une étude sans conclusion :
 * - `suivre_la_tache` : le moteur la compose (« en_cours ») ; relire, ne
 *   jamais appeler la route ;
 * - `relire_puis_demander` : règle d'avant (« echouee » ou clé absente, étude
 *   calculée il y a peu) ; relecture courte, puis un seul appel ;
 * - `demander` : un seul appel, sans attendre ;
 * - `rien` : le moteur a fini (« terminee ») ; on lit ce qu'il a écrit.
 */
export type ConduiteNarration =
    | "suivre_la_tache"
    | "relire_puis_demander"
    | "demander"
    | "rien"

/**
 * Décide la conduite d'après `outputs.narration` (courtage#274, DEV-2311).
 * Une narration « en_cours » posée depuis plus de `NARRATION_FENETRE_TACHE_MS`
 * est celle d'une tâche morte : la garde du moteur
 * (`OPTIMTURPE_NARRATION_DELAI_GARDE_S`, 120 s) laisse alors la route la
 * composer, et la page l'appelle une fois.
 */
export function conduiteNarration(
    etude: OptimTurpeV2Etude,
    maintenant: number = Date.now()
): ConduiteNarration {
    const statut = etude.narration?.statut
    if (statut === "terminee") return "rien"
    if (statut === "en_cours") {
        const depuis = lireInstant(etude.narration?.depuis)
        if (
            depuis !== null &&
            maintenant - depuis >= NARRATION_FENETRE_TACHE_MS
        ) {
            return "demander"
        }
        return "suivre_la_tache"
    }
    // « echouee » ou clé absente : le comportement d'avant.
    if (!etude.calcul) return "demander"
    const calculeLe = lireInstant(etude.calcul.calcule_le)
    if (calculeLe === null) return "relire_puis_demander"
    return maintenant - calculeLe < NARRATION_FENETRE_TACHE_MS
        ? "relire_puis_demander"
        : "demander"
}

/**
 * Préfixe des causes que le moteur NOMME sur le chemin du rapport (DEV-1914). Un 404 qui en
 * porte une concerne une étude ; un 404 qui n'en porte aucune est une route absente de cet
 * environnement. Sans cette distinction, un « étude introuvable » retirerait durablement une
 * fonction qui marche.
 */
export const PREFIXE_CAUSE_RAPPORT = "optimturpe-"

/**
 * Store Optim TURPE **v2** (DEV-715). Backend v2 câblé bout-en-bout (courtage prod
 * v84 + façade api-metier `/v5/optimturpe/v2/*`) → `useMock=false` par défaut (service
 * réel). Les stories Storybook forcent `useMock=true` explicitement pour servir les
 * fixtures sans backend.
 */
export const useOptimTurpeV2Store = defineStore("optimTurpeV2", () => {
    const service = new OptimTurpeV2Service()

    const useMock = ref<boolean>(false)
    const currentEtude = ref<OptimTurpeV2Etude | null>(null)
    const currentSourceId = ref<string>("")
    const isRunning = ref<boolean>(false)
    const runError = ref<string | null>(null)
    /**
     * Phase de RELECTURE (DEV-1583) : le POST a été coupé, on relit la liste des
     * études pour savoir si le calcul a quand même abouti. `isRunning` reste vrai
     * (le calcul tourne réellement), mais un changement de compteur peut préempter
     * cette phase — contrairement à la phase POST, gardée contre la concurrence.
     */
    const isRelisant = ref<boolean>(false)
    /**
     * Numéro du run courant. Un nouveau run l'incrémente : une relecture dont le
     * numéro n'est plus le courant a été PRÉEMPTÉE et n'écrit plus rien (sinon le
     * résultat d'un compteur abandonné écraserait celui du compteur sélectionné).
     */
    let runSeq = 0
    /**
     * Contrôleur de la lecture en vol pendant une relecture. Abandonner la page ou
     * lancer un autre run doit couper la requête, pas seulement ignorer sa réponse
     * (revue 03/09, correction 4).
     */
    let relectureController: AbortController | null = null
    /**
     * Numéro de la relecture de narration en vol (DEV-2311, F2). Un nouveau run,
     * une autre narration demandée, le départ de la page ou `$reset`
     * l'incrémentent : la relecture dépassée n'écrit plus rien.
     */
    let narrationSeq = 0
    /**
     * Message d'ATTENTE (DEV-1529) — un état, pas une erreur.
     *
     * Il porte « calcul en cours » pendant que le moteur travaille en file, puis,
     * au plafond de suivi, le constat que l'attente dépasse l'ordinaire. Distinct
     * de `runError` à dessein : la page sert l'un en information et l'autre en
     * erreur, et un calcul qui met du temps n'est pas une panne.
     */
    const messageAttente = ref<string | null>(null)
    /**
     * Le lancement en cours a-t-il été ACCEPTÉ par le moteur avec un
     * identifiant d'étude (DEV-2412) ? Seul ce cas permet de dire « vous pouvez
     * quitter la page : l'étude sera affichée à votre retour », parce que
     * l'ouverture reprend alors le suivi de cette étude. Avant l'acceptation,
     * ou sans identifiant, la carte d'attente ne promet rien.
     */
    const lancementAccepte = ref<boolean>(false)
    /**
     * Suivis d'études en vol (un par scénario : l'arbitrage en lance deux). Un
     * ENSEMBLE, pas une référence unique : avec une seule, le second suivi
     * écrasait la référence du premier, qui n'était alors plus jamais coupé et
     * continuait d'interroger la façade après le départ de l'utilisateur.
     */
    const suivisEnCours = new Set<AbortController>()

    // --- Rapport PDF (DEV-823, recomposé par DEV-1914) : le document est composé côté
    // courtage à partir des champs du moteur, rendu en PDF par le service `rapport`, et le
    // navigateur n'en compose plus aucune ligne.
    const isReporting = ref<boolean>(false)
    const reportError = ref<string | null>(null)
    /**
     * Le document demandé, MÉMORISÉ AVEC L'ÉTUDE dont il est le rapport.
     *
     * L'URL seule ne suffit pas : `currentEtude` est réassignée par `runBySource`,
     * `runArbitrage`, `setScenarioRetenu` et `explainCurrent`, et le store survit à la
     * navigation. Une URL gardée seule serait donc servie à l'aperçu et au téléchargement
     * après une bascule de scénario CMDPS ou un changement de compteur : le client
     * transmettrait le PDF de l'étude précédente sous les chiffres de la nouvelle.
     */
    const reportParEtude = ref<{ etudeId: string; url: string } | null>(null)
    /**
     * L'URL du document de l'étude COURANTE, ou `null` dès que l'étude change. C'est la seule
     * lecture exposée : rien ne peut lire une URL qui ne correspond plus à ce qui est affiché.
     */
    const reportUrl = computed<string | null>(() =>
        reportParEtude.value &&
        reportParEtude.value.etudeId === (currentEtude.value?._id ?? null)
            ? reportParEtude.value.url
            : null
    )
    /**
     * La route du rapport n'existe pas sur cet environnement : un 404 SANS cause métier
     * nommée. On le retient pour retirer les boutons plutôt que de laisser l'utilisateur
     * buter sur une erreur à chaque clic. Un 404 « étude introuvable », lui, porte le code
     * du moteur : c'est une erreur de cette étude-là, pas une fonction absente.
     */
    const rapportIndisponible = ref<boolean>(false)

    // --- Arbitrage CMDPS (Z3, DEV-783) : 2 scénarios calculés (sans / avec dépassement) ---
    const etudeSansCmdps = ref<OptimTurpeV2Etude | null>(null)
    const etudeAvecCmdps = ref<OptimTurpeV2Etude | null>(null)
    /** Scénario actuellement retenu — pilote `currentEtude` (donc Z1 / Z5). */
    const scenarioRetenu = ref<OptimTurpeV2Scenario>("avec")
    /**
     * Lot 4g (C25, D17 (a)) : l'échec de l'enregistrement du scénario retenu,
     * dit sous les deux tuiles ; l'affichage revient alors au scénario
     * enregistré, celui que la page relirait au rechargement.
     */
    const scenarioErreur = ref<string | null>(null)
    /**
     * Le scénario que le rechargement montrerait : `scenario_retenu` servi
     * (ou, à défaut, le conseil) à l'ouverture, puis le dernier confirmé par
     * la route scénario. C'est lui, et non le scénario affiché avant le clic,
     * que l'affichage retrouve après un échec.
     */
    let scenarioEnregistre: OptimTurpeV2Scenario = "avec"
    /** Le tour du dernier « Retenir ce scénario » : une réponse plus ancienne ne pose rien. */
    let retenueSeq = 0

    /** Source (`_id_source`) sélectionnée — pilote le sélecteur de compteur de la page. */
    function setCurrentSourceId(sourceId: string): void {
        currentSourceId.value = sourceId
    }

    /**
     * Le bâtiment dont le magasin porte le compteur et l'étude (DEV-2311,
     * revue du 08/10, R4). `""` tant qu'aucune page n'est entrée.
     */
    const batiment = ref<string>("")

    /**
     * La page entre sur un bâtiment : si ce n'est pas celui du magasin, le
     * magasin se vide AVANT tout rendu (R4).
     *
     * Le magasin survit à la navigation : passer de la page d'un bâtiment à
     * celle d'un autre gardait le compteur et l'étude du premier. La page du
     * second les rendait un instant, et lisait le compteur du premier, avant de
     * les remplacer par son état d'entrée : « j'avais une étude, elle a
     * disparu ». Le retour sur le MÊME bâtiment, lui, garde tout.
     */
    function entrerSurBatiment(buildingId: string): void {
        if (batiment.value === buildingId) return
        $reset()
        batiment.value = buildingId
    }

    /** Une étude au-delà de cet âge n'est plus « récente » (R4). */
    const AGE_MAX_ETUDE_AILLEURS_MS = 30 * 24 * 3600 * 1000

    /**
     * Le bâtiment de la dernière étude du compte, quand elle est récente et
     * porte sur un AUTRE bâtiment que `buildingId`, avec son nom ; sinon `null`
     * (R4). Sert à l'état d'entrée d'un bâtiment sans étude : « Votre dernière
     * étude est sur le bâtiment … ». Toute lecture qui ne répond pas, ou un nom
     * absent, rend `null` : la ligne ne s'affiche pas, rien d'autre ne change.
     */
    async function derniereEtudeAilleurs(
        buildingId: string,
        maintenant: number = Date.now()
    ): Promise<{ buildingId: string; nom: string } | null> {
        if (useMock.value || !buildingId || !CYCLE_ROUTES_ACTIVES) return null
        try {
            const derniere = await service.derniereEtudeDuCompte()
            if (!derniere || derniere.buildingId === buildingId) return null
            const creee = lireInstant(derniere.creeeLe) ?? NaN
            if (
                !Number.isFinite(creee) ||
                maintenant - creee > AGE_MAX_ETUDE_AILLEURS_MS
            )
                return null
            const nom = await service.nomDuBatiment(derniere.buildingId)
            return nom ? { buildingId: derniere.buildingId, nom } : null
        } catch {
            return null
        }
    }

    /**
     * État de l'étude affichée, **lu** sur ce que le moteur sert (DEV-1907). `null`
     * quand il n'y a pas d'état : une absence n'est ni un 0 ni « déjà optimale ».
     */
    const currentState = computed<OptimTurpeV2EtatServi | null>(() =>
        currentEtude.value ? resolveOptimTurpeV2State(currentEtude.value) : null
    )

    // --- Périmètre servi par le moteur (DEV-1912) : jamais recopié par la page ---
    /**
     * `null` = périmètre INCONNU, et c'est un état plein : la page ne pré-annonce
     * alors rien et laisse lancer l'étude, dont le refus nommé portera la cause
     * vraie. Un « couvert » ou un « non couvert » présumé serait le défaut qu'on
     * corrige (DEV-1678).
     */
    const perimetre = ref<OptimTurpeV2Perimetre | null>(null)
    /** Le périmètre a-t-il été demandé ? Évite de le redemander à chaque compteur. */
    let perimetreDemande = false

    /**
     * Lit le périmètre une fois par session de page. Un échec est SILENCIEUX à
     * dessein : la route de façade n'existe pas encore (404), et peindre la page en
     * rouge pour une information facultative ferait croire à une panne de l'étude.
     */
    async function chargerPerimetre(): Promise<void> {
        if (!PERIMETRE_SERVI_ACTIF || perimetreDemande || useMock.value) return
        perimetreDemande = true
        try {
            perimetre.value = (await service.getPerimetre()) ?? null
        } catch {
            perimetre.value = null
        }
    }

    // --- Refus indépendants du scénario, par compteur (DEV-2189) ---
    /**
     * Le refus que le dernier lancement de chaque compteur a reçu, quand il ne
     * dépend pas du scénario (`compteur-resilie`, `collecte-interrompue`). Il
     * évite de relancer la paire pour un refus déjà su, et date la collecte
     * arrêtée dans le sélecteur de compteurs.
     */
    const refusConnus = ref<Record<string, RefusConnu>>({})

    function noterRefus(
        sourceId: string | null | undefined,
        etudes: Array<OptimTurpeV2Etude | null | undefined>
    ): void {
        if (!sourceId) return
        const connu = refusConnuDe(etudes)
        const suivant = { ...refusConnus.value }
        if (connu) suivant[sourceId] = connu
        else delete suivant[sourceId]
        refusConnus.value = suivant
    }

    // --- Historique du compteur (A12) : les études, leur date, leur suppression ---
    const historique = ref<OptimTurpeV2Etude[]>([])
    const isChargementHistorique = ref<boolean>(false)
    const isSuppressionEnCours = ref<boolean>(false)
    const historiqueError = ref<string | null>(null)
    /**
     * La liste du cycle a-t-elle été coupée par sa limite ? `null` = non dit. La
     * troncature se lit DANS la liste, jamais par une bannière.
     */
    const historiqueTronque = ref<boolean | null>(null)

    // --- Gestes du cycle de vie (DEV-2150) : rejouer, rétablir, supprimer ---
    /** Le geste en cours, et le lancement qu'il vise (la ligne ne se clique pas deux fois). */
    const geste = ref<{
        type: "rejouer" | "retablir" | "supprimer"
        ids: string[]
    } | null>(null)
    /** L'échec NOMMÉ du dernier geste, dit près de l'historique. */
    const gesteErreur = ref<string | null>(null)

    // --- Refus nommé (DEV-2163) ---
    /**
     * L'étude REFUSÉE par le moteur au dernier lancement, ou `null`.
     *
     * Avant ce lot, un refus ne laissait qu'une phrase (`runError`) : la page
     * perdait le code, le levier et la période demandée, retirait « Affiner
     * l'étude » et gardait l'en-tête de l'étude d'avant. Le refus est désormais
     * gardé ENTIER, pour que la page laisse les réglages ouverts sur la période
     * saisie et propose le remède. `runError` porte toujours la phrase.
     */
    const refus = ref<OptimTurpeV2Etude | null>(null)
    /** La période demandée au lancement en cours, pour la redire après un refus. */
    let periodeDemandee: { date_from: string; date_to: string } | null = null
    /**
     * La source dont l'ouverture est en cours (DEV-2156). Posée AVANT toute
     * attente : deux déclencheurs concurrents (montage de la page, arrivée des
     * compteurs) ne peuvent plus lancer deux fois.
     */
    let ouvertureEnCours: string | null = null
    /**
     * Numéro de la DERNIÈRE ouverture demandée. Une ouverture dépassée (le
     * client est passé de A à B puis revenu à A pendant le chargement) n'écrit
     * plus rien : ni l'étude, ni l'historique, ni un lancement.
     */
    let ouvertureSeq = 0

    /**
     * Charge les études de la company et garde celles du compteur affiché.
     *
     * Le filtre se fait sur `_id_source` des `inputs`, l'identifiant que la page
     * utilise pour lancer l'étude — `source.idsource` est le numéro de PDL, qui
     * n'identifie pas la source dans notre référentiel.
     */
    async function chargerHistorique(sourceId: string): Promise<void> {
        if (useMock.value || !sourceId) return
        isChargementHistorique.value = true
        historiqueError.value = null
        try {
            if (CYCLE_ROUTES_ACTIVES) {
                // La liste du CYCLE du compteur (DEV-2150) : filtrée par le
                // moteur, études remplacées comprises, supprimées exclues.
                const liste = await service.listCycle(sourceId)
                // La liste d'un compteur quitté pendant la lecture ne
                // s'affiche jamais sous le compteur courant.
                if (currentSourceId.value && currentSourceId.value !== sourceId)
                    return
                historique.value = trierParDateDeCalcul(liste.etudes)
                historiqueTronque.value = liste.tronquee
                noterRefus(
                    sourceId,
                    lancementAOuvrir(historique.value)?.etudes ?? []
                )
            } else {
                const toutes = await service.listEtudes()
                if (currentSourceId.value && currentSourceId.value !== sourceId)
                    return
                historique.value = trierParDateDeCalcul(
                    toutes.filter(
                        (etude) => etude.inputs?.source?._id_source === sourceId
                    )
                )
                historiqueTronque.value = null
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                historiqueError.value = resolveServiceErrorMessage(error, {
                    surPanne:
                        "La liste des études précédentes n'a pas pu être chargée pour une raison technique. Réessayez dans un instant.",
                    surAutre: (status) =>
                        `La liste des études précédentes n'a pas pu être chargée. ${consigneApresRefus(status)}`,
                })
            }
        } finally {
            isChargementHistorique.value = false
        }
    }

    /**
     * Les compteurs étudiés du bâtiment, le plus récemment étudié d'abord
     * (DEV-2311, revue du 08/10, R4). Sert à rouvrir, sans `?idsource=`, le
     * compteur que le client vient d'étudier. Une liste vide veut dire « aucune
     * étude » ; une liste illisible rend `null` : la page ne conclut alors
     * rien sur le bâtiment et retombe sur le premier compteur éligible.
     */
    async function sourcesEtudieesDuBatiment(
        buildingId: string
    ): Promise<string[] | null> {
        if (useMock.value || !buildingId || !CYCLE_ROUTES_ACTIVES) return null
        try {
            return await service.listSourcesEtudiees(buildingId)
        } catch {
            return null
        }
    }

    /**
     * Affiche les études d'un LANCEMENT (un ou deux scénarios), sans rien
     * recalculer (A12, geste de lecture). Les deux scénarios reposent
     * l'arbitrage ; un scénario seul s'affiche seul.
     */
    function afficherLancement(etudes: OptimTurpeV2Etude[]): void {
        reporterScenarioRetenu(etudes)
        const sans =
            etudes.find((e) => e.inputs?.optimisation?.allow_cmdps === false) ??
            null
        const avec =
            etudes.find((e) => e.inputs?.optimisation?.allow_cmdps === true) ??
            null
        refus.value = null
        if (sans && avec) {
            appliquerArbitrage(sans, avec)
            return
        }
        etudeSansCmdps.value = null
        etudeAvecCmdps.value = null
        currentEtude.value = etudes[0] ?? null
    }

    /**
     * Lot 4g (C25) : la liste du cycle, qui remplit l'historique, sert déjà
     * `scenario_retenu` (courtage, `optimturpe_cycle.py` : la liste est servie
     * avec `champs_page=True`, comme la lecture d'une étude). Le report reste
     * utile pour une étude relue individuellement après le chargement de la
     * liste et ne nuit pas : il recopie ce que
     * la relecture de la même étude a servi, sans rien inventer ; une ligne
     * jamais relue garde ce que la liste a servi.
     */
    function reporterScenarioRetenu(lues: OptimTurpeV2Etude[]): void {
        const servis = new Map<string, string>()
        for (const e of lues)
            if (e._id && typeof e.scenario_retenu === "string")
                servis.set(String(e._id), e.scenario_retenu)
        if (servis.size === 0) return
        historique.value = historique.value.map((e) =>
            servis.has(String(e._id))
                ? { ...e, scenario_retenu: servis.get(String(e._id)) }
                : e
        )
    }

    /** Ouvre un lancement de l'historique par ses identifiants, sans recalcul. */
    async function ouvrirLancement(
        ids: string[],
        toujoursCourante: () => boolean = () => true
    ): Promise<void> {
        const cibles = ids.filter(Boolean)
        if (useMock.value || cibles.length === 0) return
        runError.value = null
        try {
            const etudes = await Promise.all(
                cibles.map((id) => service.getEtude(id))
            )
            if (!toujoursCourante()) return
            const lues = etudes.filter(
                (e): e is OptimTurpeV2Etude => e !== undefined && e !== null
            )
            if (lues.length > 0) afficherLancement(lues)
        } catch (error) {
            applyRunError(error)
        }
    }

    /** Ouvre une étude déjà calculée, sans rien recalculer (A12, geste de lecture). */
    async function ouvrirEtude(analyseId: string): Promise<void> {
        await ouvrirLancement([analyseId])
    }

    /**
     * L'OUVERTURE de la page sur un compteur (DEV-2156, décision du pilote du 25/09).
     *
     * Avant : chaque ouverture lançait une étude (deux POST, un par scénario),
     * et l'historique se remplissait d'une paire d'études identiques à chaque
     * visite. Désormais :
     * - une étude non supprimée existe déjà : la plus récente COURANTE est
     *   affichée, sans aucun calcul ;
     * - il n'en existe aucune : un seul lancement part ;
     * - la liste ne répond pas : rien ne part sans geste, et la page le dit ;
     * - un lancement est encore en file (DEV-2311, F3) : la page reprend son
     *   SUIVI au lieu d'en relancer un second.
     *
     * Rend `affichee`, `lancee`, `suivie`, `rien`, ou `ignoree` pour un
     * déclencheur surnuméraire sur la même source.
     */
    async function ouvrirSource(
        sourceId: string,
        base: OptimTurpeV2RunBody = {},
        options: OptionsLancement = {},
        /**
         * Parcours guidé (DEV-2311, 07/10/2026) : la page ne lance plus
         * d'elle-même l'étude d'un compteur qui n'en a aucune ; elle montre les
         * paramètres recommandés et attend « Lancer l'étude recommandée ».
         * Faux : l'ouverture s'arrête à `"rien"` quand aucune étude n'existe.
         */
        { lancerSiAucune = true }: { lancerSiAucune?: boolean } = {}
    ): Promise<"affichee" | "lancee" | "suivie" | "rien" | "ignoree"> {
        if (useMock.value || !sourceId) return "rien"
        if (ouvertureEnCours === sourceId) return "ignoree"
        if (isRunning.value && currentSourceId.value === sourceId) {
            return "ignoree"
        }
        ouvertureSeq += 1
        const monOuverture = ouvertureSeq
        const estCourante = () =>
            monOuverture === ouvertureSeq && currentSourceId.value === sourceId
        ouvertureEnCours = sourceId
        // Les études d'un lancement encore en file, dont on reprend le suivi.
        let aSuivre: string[] | null = null
        try {
            if (currentSourceId.value !== sourceId) {
                // Un autre compteur : l'écran ne garde rien du précédent.
                currentEtude.value = null
                etudeSansCmdps.value = null
                etudeAvecCmdps.value = null
                refus.value = null
                runError.value = null
                historique.value = []
            }
            currentSourceId.value = sourceId
            await chargerHistorique(sourceId)
            if (!estCourante()) return "ignoree"
            if (historiqueError.value) return "rien"
            const enCours = lancementEnCours(historique.value)
            if (enCours) aSuivre = enCours.ids
            // Décision (q) du 20/10/2026 (contrat v1.6 §8.4.14) : à l'arrivée,
            // la recommandation, en lecture seule ; jamais un rétablissement.
            const lancement = aSuivre
                ? null
                : lancementAOuvrirALArrivee(historique.value)
            if (lancement) {
                await ouvrirLancement(lancement.ids, estCourante)
                return estCourante() ? "affichee" : "ignoree"
            }
        } finally {
            // Seule l'ouverture la plus récente libère le verrou : une ouverture
            // dépassée ne rouvre pas la porte à un doublon de la suivante.
            if (monOuverture === ouvertureSeq) ouvertureEnCours = null
        }
        if (!estCourante()) return "ignoree"
        if (aSuivre) {
            await reprendreSuivi(aSuivre)
            if (!estCourante()) return "ignoree"
            await chargerHistorique(sourceId)
            return "suivie"
        }
        if (!lancerSiAucune) return "rien"
        try {
            await runArbitrage(sourceId, base, options)
        } catch {
            // L'échec est déjà porté par `runError` ou `refus` : la page le dit.
        }
        await chargerHistorique(sourceId)
        return "lancee"
    }

    /**
     * Reprend le SUIVI d'un lancement encore en file (DEV-2311, F3) : même
     * suivi qu'après un POST, sans rien relancer. L'écran dit « calcul en
     * cours », puis affiche les études quand elles aboutissent.
     */
    async function reprendreSuivi(ids: string[]): Promise<void> {
        const cibles = ids.filter(Boolean)
        if (useMock.value || cibles.length === 0 || isRunning.value) return
        const monTour = commencerRun()
        try {
            const etudes = await Promise.all(
                cibles.map((id) =>
                    attendreResultat(
                        { etat: "en_cours", analyseId: id, etude: null },
                        monTour
                    )
                )
            )
            if (monTour !== runSeq) return
            const abouties = etudes.filter(
                (e): e is OptimTurpeV2Etude => e !== null
            )
            // Un demi-lancement ne s'affiche pas comme un arbitrage : même
            // règle qu'au lancement.
            if (abouties.length !== cibles.length) return
            afficherLancement(abouties)
            noterRefus(currentSourceId.value, abouties)
            await explainCurrent()
        } finally {
            terminerRun(monTour)
        }
    }

    /** La phrase d'une panne de notre côté, pour un geste ou un calcul. */
    function messagePanne(geste: string): string {
        return `${geste} n'a pas abouti pour une raison technique. Réessayez dans un instant.`
    }

    /** Message d'échec NOMMÉ d'un geste du cycle : la cause servie d'abord. */
    function nommerEchecGeste(error: unknown, geste: string): string {
        return resolveServiceErrorMessage(error, {
            surPanne: messagePanne(geste),
            surAutre: (status) =>
                `${geste} n'a pas abouti. ${consigneApresRefus(status)}`,
        })
    }

    /**
     * Supprime les études d'un lancement, LOGIQUEMENT (DEV-2065, DEV-2150),
     * après confirmation DANS la page. Le document reste lisible au support.
     *
     * Si l'étude affichée était du lancement, la page rouvre la courante suivante
     * de l'historique, sans rien recalculer.
     */
    async function supprimerLancement(ids: string[]): Promise<void> {
        const cibles = ids.filter(Boolean)
        if (useMock.value || cibles.length === 0) return
        geste.value = { type: "supprimer", ids: cibles }
        isSuppressionEnCours.value = true
        gesteErreur.value = null
        historiqueError.value = null
        try {
            await Promise.all(cibles.map((id) => service.supprimerEtude(id)))
            historique.value = historique.value.filter(
                (etude) => !cibles.includes(String(etude._id ?? ""))
            )
            if (
                currentEtude.value?._id &&
                cibles.includes(currentEtude.value._id)
            ) {
                const suivant = lancementAOuvrir(historique.value)
                if (suivant) {
                    afficherLancement(suivant.etudes)
                } else {
                    currentEtude.value = null
                    etudeSansCmdps.value = null
                    etudeAvecCmdps.value = null
                }
            }
        } catch (error) {
            if (!axios.isCancel(error)) {
                gesteErreur.value = nommerEchecGeste(error, "La suppression")
                historiqueError.value = gesteErreur.value
            }
        } finally {
            isSuppressionEnCours.value = false
            geste.value = null
        }
        if (currentSourceId.value)
            await chargerHistorique(currentSourceId.value)
    }

    /** Compatibilité : suppression d'une étude seule. */
    async function supprimerEtude(analyseId: string): Promise<void> {
        await supprimerLancement([analyseId])
    }

    /**
     * RÉTABLIT un lancement antérieur comme courant, SANS recalcul (DEV-2065).
     * Aucun POST de lancement : les montants affichés sont ceux qui avaient été
     * servis.
     */
    async function retablirLancement(ids: string[]): Promise<void> {
        const cibles = ids.filter(Boolean)
        if (useMock.value || cibles.length === 0) return
        geste.value = { type: "retablir", ids: cibles }
        gesteErreur.value = null
        try {
            const etudes = await Promise.all(
                cibles.map((id) => service.retablirEtude(id))
            )
            const lues = etudes.filter(
                (e): e is OptimTurpeV2Etude => e !== undefined && e !== null
            )
            if (lues.length > 0) afficherLancement(lues)
        } catch (error) {
            if (!axios.isCancel(error)) {
                gesteErreur.value = nommerEchecGeste(error, "Le rétablissement")
            }
        } finally {
            geste.value = null
        }
        if (currentSourceId.value)
            await chargerHistorique(currentSourceId.value)
    }

    /**
     * La marge que l'utilisateur a CHOISIE, par compteur, pendant la visite
     * (fraction envoyée par le bloc d'affinage). Une actualisation ou une
     * relance sur une autre période la reprend au lieu de revenir au défaut
     * « Équilibré » (retour de relecture du 07/10).
     */
    const margesChoisies = ref<Record<string, number>>({})

    function noterMargeChoisie(sourceId: string, optimisation: unknown): void {
        const v = (optimisation as { marge_securite_pct?: unknown } | undefined)
            ?.marge_securite_pct
        if (sourceId && typeof v === "number" && Number.isFinite(v)) {
            margesChoisies.value = { ...margesChoisies.value, [sourceId]: v }
        }
    }

    /**
     * REJOUE un lancement (DEV-2065) : une étude NEUVE par scénario, chaînée à
     * l'ancienne, qui reste lisible et marquée « remplacée le … ». Les calculs mis
     * en file sont suivis jusqu'à leur terme, comme un lancement.
     */
    async function rejouerLancement(
        ids: string[],
        surcharges?: Record<string, unknown> | null
    ): Promise<void> {
        // Des bornes NULLES demandent la fenêtre du moteur (DEV-2178) : aucune
        // période n'est alors « demandée » par la page.
        const data = surcharges?.data as
            | { date_from?: string | null; date_to?: string | null }
            | undefined
        let cibles = ids.filter(Boolean)
        if (useMock.value || cibles.length === 0) return
        if (isRunning.value) return
        // Retour du 07/10 : le rejeu porte la marge choisie, sinon « Équilibré ».
        noterMargeChoisie(currentSourceId.value, surcharges?.optimisation)
        const rejouee = [
            etudeSansCmdps.value,
            etudeAvecCmdps.value,
            currentEtude.value,
        ].find((e) => e?._id && cibles.includes(e._id))
        surcharges = surchargesAvecMargeParDefaut(
            surcharges,
            rejouee?.inputs?.optimisation,
            margesChoisies.value[currentSourceId.value]
        )
        // DEV-2189 : un refus déjà su, indépendant du scénario, ne se rejoue
        // qu'une fois, sur la jambe « sans » (celle qui porte le refus).
        if (
            cibles.length > 1 &&
            refusSApplique(
                refusConnus.value[currentSourceId.value],
                data?.date_from,
                saisiesDuLancement(surcharges)
            )
        ) {
            const sans = etudeSansCmdps.value?._id
            cibles = [sans && cibles.includes(sans) ? sans : cibles[0]]
        }
        geste.value = { type: "rejouer", ids: cibles }
        gesteErreur.value = null
        const monTour = commencerRun()
        periodeDemandee =
            data?.date_from && data?.date_to
                ? { date_from: data.date_from, date_to: data.date_to }
                : null
        try {
            const lancements = await Promise.all(
                cibles.map((id) => service.rejouerEtude(id, surcharges))
            )
            if (monTour !== runSeq) return
            const etudes = await Promise.all(
                lancements.map((l) => attendreResultat(l, monTour))
            )
            if (monTour !== runSeq) return
            const abouties = etudes.filter(
                (e): e is OptimTurpeV2Etude => e !== null
            )
            if (abouties.length === cibles.length) {
                afficherLancement(abouties)
                // Même règle qu'un lancement : la narration du scénario retenu.
                await explainCurrent()
            }
        } catch (error) {
            // Un refus NOMMÉ de l'étude (4xx du moteur hors cycle, par exemple
            // `segment-incoherent-avec-fta`, DEV-2176) se pose comme au
            // lancement : la page dit la cause et garde les réglages ouverts,
            // la phrase à côté du champ. Un refus du GESTE (`cycle-…`) reste
            // l'erreur du geste.
            if (
                !axios.isCancel(error) &&
                (estRefusDeGeste(error) ||
                    !poserRefusDepuisErreur(error, currentSourceId.value))
            ) {
                gesteErreur.value = nommerEchecGeste(error, "Le rejeu")
            }
        } finally {
            terminerRun(monTour)
            geste.value = null
        }
        if (currentSourceId.value)
            await chargerHistorique(currentSourceId.value)
    }

    /**
     * Les études AFFICHÉES qu'un geste « Relancer » rejoue (DEV-2150) : la paire
     * de l'arbitrage, ou l'étude seule. Vide quand elles ne se rejouent pas
     * (remplacées, en calcul, refus à l'écran) : la relance crée alors une étude
     * neuve.
     */
    const idsAffichesRejouables = computed<string[]>(() => {
        if (refus.value || !CYCLE_ROUTES_ACTIVES) return []
        const affichees =
            etudeSansCmdps.value && etudeAvecCmdps.value
                ? [etudeSansCmdps.value, etudeAvecCmdps.value]
                : [currentEtude.value]
        return idsRejouables(affichees)
    })

    /**
     * Pose le refus NOMMÉ d'un lancement (DEV-2163). La période demandée est
     * reprise quand le document ne la porte pas, pour que la page la redise.
     */
    function poserRefus(etude: OptimTurpeV2Etude): void {
        noterRefus(etude.inputs?.source?._id_source ?? currentSourceId.value, [
            etude,
        ])
        const data = etude.inputs?.data ?? periodeDemandee
        refus.value = {
            ...etude,
            status: etude.status ?? "FAILURE",
            inputs: {
                ...(etude.inputs ?? {}),
                ...(data ? { data } : {}),
            } as OptimTurpeV2Etude["inputs"],
        }
    }

    /**
     * Un refus SYNCHRONE (4xx nommé par le moteur, relayé par la façade) :
     * reconstitué en étude refusée, avec sa cause, son explication et la période
     * demandée. 401, 403 et 404 ne sont pas des refus de l'étude.
     */
    function poserRefusDepuisErreur(error: unknown, sourceId: string): boolean {
        if (!(error instanceof ServiceError)) return false
        const status = error.status ?? 0
        if (status < 400 || status >= 500 || [401, 403, 404].includes(status)) {
            return false
        }
        if (!error.code || error.code === "UNKNOWN") return false
        // Une erreur TECHNIQUE (`ERR_BAD_REQUEST`, « Request failed with status
        // code 422 ») n'est pas une cause du catalogue (DEV-2311, constat du
        // 07/10) : le client lit la phrase de panne, jamais le message brut, et
        // les réglages restent ouverts. Le code reste lisible par l'équipe.
        const technique = estErreurTechnique(error)
        poserRefus({
            status: "FAILURE",
            error: {
                code: error.code,
                message: technique ? messagePanne("Le calcul") : error.message,
                description: technique ? null : error.description,
            },
            inputs: {
                source: { _id_source: sourceId },
            } as OptimTurpeV2Etude["inputs"],
        } as OptimTurpeV2Etude)
        return true
    }

    /** Le refus porte-t-il sur le GESTE du cycle (`cycle-…`), pas sur l'étude ? */
    function estRefusDeGeste(error: unknown): boolean {
        return (
            error instanceof ServiceError &&
            typeof error.code === "string" &&
            error.code.startsWith("cycle-")
        )
    }

    /**
     * L'arbitrage CMDPS (Z3) n'est montré que s'il est RÉEL : les deux scénarios doivent
     * exister ET aboutir à une économie ferme différente. Sinon (pas de pic à arbitrer), ils
     * sont identiques → on n'affiche pas une comparaison vide.
     */
    const getArbitragePertinent = computed<boolean>(() => {
        const s = etudeSansCmdps.value
        const a = etudeAvecCmdps.value
        if (!s || !a) return false
        const fermeSans = getEconomieFermeEur(s)
        const fermeAvec = getEconomieFermeEur(a)
        // Une économie ferme non servie ne prouve PAS un arbitrage (revue C4) :
        // sans les deux montants, on ne peut pas dire que les scénarios
        // diffèrent, et on n'affiche pas une comparaison qu'on n'a pas.
        if (fermeSans === null || fermeAvec === null) return false
        return fermeSans !== fermeAvec
    })

    /**
     * Traduit une erreur en message métier lisible — jamais une cause inventée (DEV-762,
     * DEV-848 item 2, DEV-1457, DEV-1582, revue 03/09). `handleServiceCall` transforme une
     * erreur AVEC réponse en `ServiceError` (jamais l'objet axios brut, pas de `.response`) —
     * SAUF `axios.isCancel(error)`, qu'il relance TEL QUEL (l'objet cancel axios, sans aucun
     * champ `ServiceError`). Aucun appelant de `OptimTurpeV2Service` ne câble d'`AbortController`
     * aujourd'hui (inatteignable), mais la signature l'accepte sur les 6 méthodes du service : le
     * jour où un contrôleur est branché, une annulation utilisateur ne doit PAS s'afficher
     * « défaut de notre côté » — silence, ce n'est pas une erreur.
     */
    function applyRunError(error: unknown): void {
        if (axios.isCancel(error)) return
        runError.value = resolveServiceErrorMessage(error, {
            surPanne:
                "Le calcul n'a pas abouti dans le délai imparti, pour une raison technique. Réessayez dans un instant.",
            surAutre: (status) =>
                `L'étude n'a pas pu être lancée. ${consigneApresRefus(status)}`,
        })
    }

    /** Coupe la lecture en vol d'une relecture (préemption, démontage, `$reset`). */
    function annulerRelecture(): void {
        relectureController?.abort()
        relectureController = null
    }

    /** Coupe TOUS les suivis d'études en file (préemption, démontage, `$reset`). */
    function annulerSuivis(): void {
        for (const c of suivisEnCours) c.abort()
        suivisEnCours.clear()
    }

    /**
     * Abandonne le run en cours SANS toucher au résultat affiché (revue 03/09,
     * correction 4). Appelé au démontage de la page : sans lui, une relecture
     * continuait jusqu'à 90 s après le départ, émettait jusqu'à 18 `GET` et
     * réécrivait un store Pinia qui, lui, survit à la navigation.
     *
     * On n'appelle PAS `$reset` ici : vider le store ferait re-lancer une étude à
     * chaque retour sur la page. Abandonner, c'est arrêter d'écrire, pas oublier.
     */
    function abandonnerRunEnCours(): void {
        // La relecture de narration s'arrête avec la page, run ou pas.
        narrationSeq += 1
        if (!isRunning.value) return
        runSeq += 1
        annulerRelecture()
        annulerSuivis()
        isRunning.value = false
        isRelisant.value = false
        messageAttente.value = null
        lancementAccepte.value = false
    }

    /** Ouvre un run : numérote, arme les drapeaux, efface l'erreur précédente. */
    function commencerRun(): number {
        runSeq += 1
        narrationSeq += 1
        annulerRelecture()
        annulerSuivis()
        isRunning.value = true
        isRelisant.value = false
        runError.value = null
        messageAttente.value = null
        refus.value = null
        lancementAccepte.value = false
        return runSeq
    }

    /**
     * Relève, À L'INSTANT DU CLIC, les études qui existaient DÉJÀ (revue 03/09,
     * blocant 1). Une étude relue plus tard dont l'`_id` figure ici ne peut pas
     * être le résultat de ce clic : elle ne sera jamais servie à sa place.
     *
     * ⚠️ Revue 03/09 seconde passe (défaut 2) : ce relevé est ATTENDU AVANT le
     * POST, il ne part plus en parallèle. En parallèle, rien ne garantissait que
     * sa réponse précède la création des `AnalyseV2` par courtage ; or ces
     * documents existent en `PENDING` pendant tout le calcul (c'est la prémisse
     * du cas `en_cours`). S'il les voyait, leurs `_id` entraient dans l'ensemble
     * d'exclusion et la relecture ne les servait JAMAIS, même passées en SUCCESS :
     * le correctif du blocant 1 rendait inerte celui du ticket.
     *
     * L'antériorité est donc obtenue PAR CONSTRUCTION : la réponse du relevé est
     * reçue avant que le POST ne soit émis, donc avant que le moteur n'ait pu
     * créer quoi que ce soit. Le coût est une lecture de liste (quelques centaines
     * de ms) devant un POST qui dure une trentaine de secondes.
     *
     * Le croisement suggéré en revue (n'exclure un `_id` relevé que si son
     * `created_at` est antérieur au clic) n'est PAS retenu : il rouvre le blocant 1
     * dès que l'horloge du navigateur retarde sur celle du serveur, une étude
     * réellement ancienne sortant alors du jeu d'exclusion pour repasser par la
     * tolérance de 60 s. L'attente, elle, ne coûte rien à la sûreté.
     *
     * S'il échoue, on le dit (`fiable: false`) et la relecture bascule sur un
     * plancher temporel STRICT. Les études déjà à l'écran sont ajoutées dans les
     * deux cas : c'est le cas exact du re-clic « Affiner l'étude ».
     */
    function releverEtudesAvantClic(): Promise<{
        ids: Set<string>
        fiable: boolean
    }> {
        const ids = new Set<string>()
        for (const e of [
            currentEtude.value,
            etudeSansCmdps.value,
            etudeAvecCmdps.value,
        ]) {
            if (e?._id) ids.add(String(e._id))
        }
        if (useMock.value) return Promise.resolve({ ids, fiable: true })
        return service
            .listEtudes()
            .then((liste) => {
                for (const e of liste) if (e._id) ids.add(String(e._id))
                return { ids, fiable: true }
            })
            .catch(() => ({ ids, fiable: false }))
    }

    /** Ferme un run, SAUF s'il a été préempté par un run plus récent. */
    function terminerRun(monTour: number): void {
        if (monTour !== runSeq) return
        isRunning.value = false
        isRelisant.value = false
        lancementAccepte.value = false
        annulerRelecture()
        annulerSuivis()
        // « Calcul en cours » ne survit PAS à la fin du run : il décrirait alors un
        // travail que plus rien ne suit. Le constat du plafond, lui, survit — c'est
        // la seule chose que l'écran ait à dire quand le run se termine sans étude.
        if (messageAttente.value === MESSAGE_CALCUL_EN_COURS) {
            messageAttente.value = null
        }
    }

    /**
     * Résout le lancement en une étude AFFICHABLE, ou en rien (DEV-1529).
     *
     * Deux réponses possibles de la façade, et une seule règle : on n'affiche une
     * étude que lorsqu'elle porte son résultat.
     * - `termine` : le moteur a calculé dans la requête, l'étude est servie ;
     * - `en_cours` : le calcul est en file. On le DIT (« calcul en cours »), on
     *   relit l'étude toutes les 5 s sur le chemin nominal de la façade
     *   (`GET /v5/optimturpe/v2/analyse/:id`), et on s'arrête à son état terminal.
     *
     * Rend `null` quand il n'y a rien à afficher ; l'écran porte alors soit la
     * cause NOMMÉE par le moteur (`runError`), soit le constat d'attente
     * (`messageAttente`). Jamais une cause inventée, jamais une promesse.
     */
    async function attendreResultat(
        resultat: OptimTurpeV2RunResult,
        monTour: number
    ): Promise<OptimTurpeV2Etude | null> {
        if (resultat.etat === "termine") return resultat.etude
        // Le fait, dit tout de suite : le calcul est parti, il n'est pas fini.
        messageAttente.value = MESSAGE_CALCUL_EN_COURS
        const analyseId = resultat.analyseId
        if (!analyseId) {
            // Le moteur a pris le calcul en charge sans rendre d'identifiant : il
            // n'y a rien à relire. On ne fabrique ni résultat ni cause, on dit ce
            // que le client peut faire, sans promettre l'affichage au retour :
            // l'écran n'a aucune étude à retrouver (DEV-2412).
            messageAttente.value = MESSAGE_CALCUL_SANS_SUIVI
            return null
        }
        // Accepté avec un identifiant : l'ouverture saura reprendre ce suivi,
        // la carte d'attente peut le dire (DEV-2412).
        if (monTour === runSeq) lancementAccepte.value = true
        const controller = new AbortController()
        suivisEnCours.add(controller)
        // La dernière étude lue : sur un refus nommé, c'est ELLE que la page
        // garde (DEV-2163), pas seulement sa phrase.
        let derniere: OptimTurpeV2Etude | null = null
        const issue = await suivreCalculEnCours({
            lire: async () => {
                derniere =
                    (await service.getEtude(analyseId, controller)) ?? null
                return derniere
            },
            attendre: (ms) =>
                new Promise<void>((resolve) => setTimeout(resolve, ms)),
            maintenant: () => Date.now(),
            estAnnulee: () => monTour !== runSeq || controller.signal.aborted,
        })
        suivisEnCours.delete(controller)
        // Préempté par un run plus récent : on n'écrit RIEN (ni étude, ni message).
        if (monTour !== runSeq || issue.issue === "abandonne") return null
        if (issue.issue === "terminee") return issue.etude
        if (issue.issue === "echec_nomme") {
            runError.value = issue.cause
            if (derniere) poserRefus(derniere)
            return null
        }
        if (issue.issue === "echec_muet") {
            runError.value = messageEtudeNonTerminale(issue.etat)
            return null
        }
        // Plafond atteint. Le calcul, lui, poursuit sa route sur le worker : cet
        // écran ne le suit plus, c'est tout ce qu'il affirme.
        messageAttente.value = MESSAGE_CALCUL_PLUS_LONG
        return null
    }

    /**
     * RELECTURE après une panne de réponse (DEV-1583) — le cœur du correctif.
     *
     * Le POST a été coupé (H12 du routeur à 30 s, ou 5xx) alors que le moteur
     * courtage, lui, écrit son résultat quand il a fini (31 s le 03/09). Avant
     * d'afficher une erreur, on relit donc la liste des études : ce qui a abouti
     * est servi au client comme si le POST avait répondu.
     *
     * Renvoie les études de ce clic, ou `null` — auquel cas `runError` porte déjà
     * le bon message : la cause servie par le moteur si l'étude a échoué en la
     * nommant, l'état RÉEL de l'étude si elle existe sans être terminée (échec
     * muet, abandon constaté, attente ; DEV-1621), sinon le repli de panne de
     * DEV-1582 (aucune cause inventée).
     */
    async function relireApresPanne(
        error: unknown,
        sourceId: string,
        scenarios: Array<boolean | undefined>,
        depuis: number,
        monTour: number,
        avantClic: { ids: Set<string>; fiable: boolean }
    ): Promise<OptimTurpeV2Etude[] | null> {
        // DEV-2418 : un lancement préempté envoie désormais ses POST ; leur
        // échec n'écrit pas d'erreur sur l'écran d'un autre run.
        if (monTour !== runSeq) return null
        if (!estPanneDeReponse(error) || !sourceId) {
            applyRunError(error)
            return null
        }
        // ⚠️ ORDRE IMPOSÉ (revue 03/09 seconde passe, défaut 3) : la PRÉEMPTION se
        // contrôle AVANT de poser `isRelisant`. Dans l'ordre inverse, un run
        // préempté (l'utilisateur a changé de compteur pendant le POST, ce que la
        // correction 3 vient d'autoriser) posait le drapeau puis sortait sans
        // jamais le reposer ; `terminerRun` sort lui aussi sans rien reposer sur ce
        // chemin. `isRelisant` restait vrai pendant le run SUIVANT, donc
        // `memeCompteurEnCours` devenait faux, et la garde anti-rafale de DEV-783
        // tombait : re-clic « Relancer » accepté, 4 POST simultanés au lieu de 2,
        // soit la saturation du moteur que ce programme corrige.
        if (monTour !== runSeq) return null
        isRelisant.value = true
        const controller = new AbortController()
        relectureController = controller
        const issue = await relireEtudesApresPanne(
            {
                lister: () => service.listEtudes(controller),
                attendre: (ms) =>
                    new Promise<void>((resolve) => setTimeout(resolve, ms)),
                maintenant: () => Date.now(),
                estAnnulee: () =>
                    monTour !== runSeq || controller.signal.aborted,
            },
            {
                sourceId,
                depuis,
                scenarios,
                idsAvantClic: avantClic.ids,
                releveAvantClicFiable: avantClic.fiable,
            }
        )
        // Préempté par un run plus récent : on n'écrit RIEN (ni étude, ni erreur).
        if (monTour !== runSeq) return null
        isRelisant.value = false
        annulerRelecture()
        if (issue.issue === "etudes") {
            // Miroir exact du POST : un échec NOMMÉ aurait fait rejeter l'appel avec
            // sa cause. On affiche donc la cause, pas une étude en échec.
            const echec = issue.etudes.find(estEchecNomme)
            if (echec) {
                poserRefus(echec)
                runError.value =
                    (echec.error?.message ?? "").trim() ||
                    (echec.error?.code ?? "").trim()
                return null
            }
            return issue.etudes
        }
        if (issue.issue === "en_cours") {
            // DEV-1621 : l'état RÉEL de l'étude, pas un « le calcul continue »
            // que rien ne soutient (échec muet, ou étude abandonnée pendant que
            // l'autre scénario du même clic aboutissait).
            runError.value = messageEtudeNonTerminale(issue.etat)
            return null
        }
        // Rien vu du tout (revue 03/09, blocant 2). `applyRunError` afficherait la
        // description servie par api-metier, écrite AVANT cette vérification et donc
        // moins informée qu'elle : le cas le moins renseigné deviendrait le plus
        // affirmatif. On garde la cause servie (règle DEV-1582) et on lui ajoute ce
        // que l'écran a MESURÉ.
        //
        // MESURÉ, justement : si aucune lecture n'a abouti (la panne coupe aussi la
        // liste, revue seconde passe défaut 1), on n'a rien vérifié du tout et on le
        // dit ainsi. Affirmer « nous avons vérifié » serait la faute du blocant 2
        // recommise sur l'autre branche.
        applyRunError(error)
        if (runError.value) {
            const observation = issue.lectureAboutie
                ? MESSAGE_AUCUNE_ETUDE_VUE
                : MESSAGE_VERIFICATION_IMPOSSIBLE
            runError.value = `${runError.value} ${observation}`
        }
        return null
    }

    /** Lance une étude pour une source (overrides leviers optionnels). */
    async function runBySource(
        sourceId: string,
        overrides: OptimTurpeV2RunBody = {}
    ): Promise<OptimTurpeV2Etude | null> {
        const monTour = commencerRun()
        const depuis = Date.now()
        let avantClic = { ids: new Set<string>(), fiable: false }
        try {
            if (useMock.value) {
                currentEtude.value = optimTurpeV2MockC4
                return currentEtude.value
            }
            currentSourceId.value = sourceId
            // ATTENDU avant le POST : sa réponse précède ainsi la création des
            // `AnalyseV2` par le moteur (revue 03/09 seconde passe, défaut 2).
            avantClic = await releverEtudesAvantClic()
            if (monTour !== runSeq) return null
            const resultat = await service.runBySource(sourceId, overrides)
            // Préempté (autre compteur, démontage) : on n'écrit pas le résultat d'un
            // run que l'utilisateur a quitté.
            if (monTour !== runSeq) return null
            // 202 : le calcul est en file, on le suit jusqu'à son état terminal.
            const etude = await attendreResultat(resultat, monTour)
            if (monTour !== runSeq) return null
            // Rien d'affichable : `runError` ou `messageAttente` porte déjà l'état
            // constaté, et l'étude précédente n'est pas remplacée par un doc vide.
            if (!etude) return null
            currentEtude.value = etude
            // Narration explain (confinée, capée, hash-cachée). La « preuve » s'appuie
            // désormais sur les stats MESURÉES du diagnostic (payload) — plus de fetch dapi
            // de courbe (variable `puissance_elec_tout` sans données + route 404, DEV-783).
            await explainCurrent()
            return currentEtude.value
        } catch (error) {
            const relues = await relireApresPanne(
                error,
                sourceId,
                [overrides.optimisation?.allow_cmdps],
                depuis,
                monTour,
                avantClic
            )
            if (monTour !== runSeq) return null
            if (!relues) throw error
            currentEtude.value = relues[0] ?? null
            await explainCurrent()
            return currentEtude.value
        } finally {
            terminerRun(monTour)
        }
    }

    /**
     * L'arbitrage « sans / avec dépassement » a-t-il un objet pour ce compteur ?
     * Non quand la page sait qu'il s'agit d'un C5, ou quand l'étude affichée de
     * ce compteur dit `cmdps_applicable: false` (courtage#249). Dans le doute
     * (segment inconnu, aucune étude affichée), la paire part comme avant.
     */
    function arbitrageApplicable(
        sourceId: string,
        options: OptionsLancement
    ): boolean {
        if (options.segment === "C5") return false
        if (options.segment) return true
        const affichee = currentEtude.value
        if (
            affichee &&
            currentSourceId.value === sourceId &&
            affichee.inputs?.source?._id_source === sourceId
        ) {
            return cmdpsApplicable(affichee)
        }
        return true
    }

    /**
     * Le lancement sera-t-il refusé quel que soit le scénario (DEV-2189) ? Oui
     * quand le périmètre servi dit le segment hors couverture, ou quand le
     * dernier lancement de ce compteur a reçu un refus indépendant du scénario
     * qui s'applique encore à la période demandée.
     */
    function refusAttendu(
        sourceId: string,
        base: OptimTurpeV2RunBody,
        options: OptionsLancement
    ): boolean {
        if (
            options.segment &&
            verdictSegment(perimetre.value, options.segment).couvert === false
        ) {
            return true
        }
        const saisies = saisiesDuLancement(base)
        return refusSApplique(refusConnus.value[sourceId], base.date_from, {
            fta: saisies.fta,
            segment: options.segment || saisies.segment,
        })
    }

    /**
     * Lance l'ARBITRAGE (Z3, DEV-783) : 2 runs en PARALLÈLE — sans dépassement (allow_cmdps=false)
     * et avec dépassement assumé (allow_cmdps=true). Le scénario au meilleur bénéfice NET (ferme −
     * cmdps) est retenu par défaut ; l'utilisateur peut basculer (`setScenarioRetenu`). `explain`
     * n'est appelé QUE sur le scénario retenu (budget LLM, charte art. 5). En mock : fixture simple.
     */
    async function runArbitrage(
        sourceId: string,
        base: OptimTurpeV2RunBody = {},
        options: OptionsLancement = {}
    ): Promise<void> {
        // Garde anti-concurrence (DEV-783) : un seul dual-run à la fois. Sans ça, une relance
        // déclenchée pendant un run en cours (rafale sur « Relancer », ou le watch getMeters qui
        // re-sync pendant le run initial) lance 2× Promise.all → 4 runs simultanés → le moteur
        // sature et renvoie des 503 (flag FAILURE). On ignore simplement le déclencheur surnuméraire.
        //
        // Deux exceptions à cette garde, toutes deux pour le même symptôme : « l'URL change,
        // l'écran ne suit pas » (la page fait `router.replace` AVANT d'appeler).
        //  1. la phase de RELECTURE (DEV-1583) : les POST y sont déjà terminés, rien n'est en vol
        //     côté moteur, donc rien à protéger ;
        //  2. un AUTRE compteur (revue 03/09, correction 3) : la garde vise les déclencheurs
        //     SURNUMÉRAIRES sur le compteur en cours (rafale sur « Relancer », re-sync du watch
        //     getMeters, qui no-ope déjà à source égale). Un compteur différent est une intention
        //     explicite de l'utilisateur ; l'ignorer pendant les ~27 s du POST laissait l'écran
        //     figé sur le compteur quitté. Le run préempté n'écrit rien (`runSeq`).
        const memeCompteurEnCours =
            isRunning.value &&
            !isRelisant.value &&
            sourceId === currentSourceId.value
        if (memeCompteurEnCours) return
        // Retour du 07/10 : un lancement porte la marge choisie, sinon « Équilibré ».
        noterMargeChoisie(sourceId, base.optimisation)
        base = corpsAvecMargeParDefaut(base, margesChoisies.value[sourceId])
        const monTour = commencerRun()
        periodeDemandee =
            base.date_from && base.date_to
                ? { date_from: base.date_from, date_to: base.date_to }
                : null
        const depuis = Date.now()
        let avantClic = { ids: new Set<string>(), fiable: false }
        let scenariosEnVol: boolean[] = [false, true]
        try {
            if (useMock.value) {
                currentEtude.value = optimTurpeV2MockC4
                etudeSansCmdps.value = null
                etudeAvecCmdps.value = null
                return
            }
            currentSourceId.value = sourceId
            // ATTENDU avant les POST : leur réponse précède ainsi la création des
            // `AnalyseV2` par le moteur (revue 03/09 seconde passe, défaut 2).
            avantClic = await releverEtudesAvantClic()
            // DEV-2412, DEV-2418 : le clic a été fait, les POST partent TOUJOURS
            // après ce relevé, une seule fois. Ni le départ de la page, ni un
            // run commencé entre-temps (autre lancement, reprise du suivi d'un
            // autre compteur) ne les retient : sur une société de plusieurs
            // centaines de bâtiments, le relevé dure assez pour que le
            // lancement suivant commence avant sa réponse, et le premier ne
            // partait jamais (constat du 18/10). La préemption ne coupe que
            // l'ÉCRITURE des réponses (`monTour !== runSeq`), et le suivi
            // reprend au retour (`ouvrirSource`, puis `reprendreSuivi`).
            const withAllow = (allow: boolean): OptimTurpeV2RunBody => ({
                ...base,
                optimisation: { ...base.optimisation, allow_cmdps: allow },
            })
            // Sans CMDPS (C5), l'arbitrage n'a pas d'objet : UN scénario, sans
            // dépassement, au lieu d'une paire dont l'un ne sert à rien. Même
            // chose quand la page sait déjà le refus (DEV-2189) : un refus qui
            // ne dépend pas du scénario ne se demande pas deux fois.
            // « Ma propre structure » (price_only) : le moteur chiffre la cible
            // saisie, quel que soit `allow_cmdps` (courtage,
            // build_best_from_cible) ; la paire rendait deux fois le même
            // résultat (DEV-2311). Une seule étude, sans dépassement.
            if (
                base.optimisation?.mode === "price_only" ||
                !arbitrageApplicable(sourceId, options) ||
                refusAttendu(sourceId, base, options)
            ) {
                scenariosEnVol = [false]
                const lance = await service.runBySource(
                    sourceId,
                    withAllow(false)
                )
                if (monTour !== runSeq) return
                const seule = await attendreResultat(lance, monTour)
                if (monTour !== runSeq || !seule) return
                appliquerArbitrage(seule, null)
                await explainCurrent()
                return
            }
            scenariosEnVol = [false, true]
            const [lanceSans, lanceAvec] = await Promise.all([
                service.runBySource(sourceId, withAllow(false)),
                service.runBySource(sourceId, withAllow(true)),
            ])
            // Préempté (autre compteur, démontage) : le résultat du compteur quitté
            // ne doit pas écraser l'écran courant.
            if (monTour !== runSeq) return
            // 202 sur l'un ou l'autre : les deux suivis courent en parallèle, comme
            // les deux POST qu'ils prolongent.
            const [sans, avec] = await Promise.all([
                attendreResultat(lanceSans, monTour),
                attendreResultat(lanceAvec, monTour),
            ])
            if (monTour !== runSeq) return
            // Un demi-arbitrage n'est pas un arbitrage : c'est déjà la règle du POST
            // (`Promise.all` rejette si l'un des deux échoue) et de la relecture
            // (`selectionnerEtudesRelues` rend `null` tant que les deux scénarios ne
            // sont pas pourvus). On ne sert pas une comparaison amputée.
            if (!sans || !avec) return
            appliquerArbitrage(sans, avec)
            await explainCurrent()
        } catch (error) {
            // Un refus NOMMÉ synchrone est gardé entier (DEV-2163).
            if (monTour === runSeq) poserRefusDepuisErreur(error, sourceId)
            // Le POST a pu être coupé alors que les deux études aboutissaient (H12
            // du routeur à 30 s, DEV-1583) : on relit avant d'afficher une erreur.
            const relues = await relireApresPanne(
                error,
                sourceId,
                scenariosEnVol,
                depuis,
                monTour,
                avantClic
            )
            if (monTour !== runSeq) return
            if (!relues) throw error
            appliquerArbitrage(relues[0] ?? null, relues[1] ?? null)
            await explainCurrent()
        } finally {
            terminerRun(monTour)
        }
    }

    /**
     * Pose les deux scénarios de l'arbitrage et l'étude affichée — même traitement
     * que les études viennent du POST ou de la relecture (DEV-1583).
     *
     * Le scénario conseillé suit la règle v2 du 12/10/2026 (DEV-2311) : assumer
     * quand la structure la plus basse n'est jamais dépassée, couvrir quand
     * assumer ne fait rien gagner, assumer quand le gain fait au moins deux fois
     * les dépassements estimés, sinon la forme du pic (DEV-783). Le moteur sert
     * ce conseil (courtage#251) ; l'écran ne le recompose qu'en repli, avec la
     * même règle sur les montants servis (`conseilDeRepli`).
     */
    function appliquerArbitrage(
        sans: OptimTurpeV2Etude | null,
        avec: OptimTurpeV2Etude | null
    ): void {
        refus.value = null
        noterRefus(
            (sans ?? avec)?.inputs?.source?._id_source ?? currentSourceId.value,
            [sans, avec]
        )
        etudeSansCmdps.value = sans
        etudeAvecCmdps.value = avec
        // Lot 4g (C25) : le scénario RETENU enregistré avec la paire
        // (`scenario_retenu`, servi à la racine) rétablit l'affichage au
        // rechargement ; sans lui, le conseil servi par le moteur
        // (`outputs.arbitrage`, DEV-2167), la règle v2 recomposée par l'écran
        // ne servant qu'en repli.
        scenarioErreur.value = null
        retenueSeq += 1
        scenarioRetenu.value =
            scenarioRetenuServi(sans, avec) ?? scenarioConseille(sans, avec)
        scenarioEnregistre = scenarioRetenu.value
        currentEtude.value =
            (scenarioRetenu.value === "sans" ? sans : avec) ?? avec ?? sans
    }

    /**
     * « Retenir ce scénario » (Z3, lot 4g, C25, D17 (a)) : bascule l'affichage,
     * puis ENREGISTRE le choix par la route scénario, une seule fois par clic.
     * L'étude rendue est celle du scénario retenu : la page en lit la demande,
     * la seconde barre et le repère du conseil. Elle remplace sa jambe, et la
     * jumelle reçoit le même `scenario_retenu`, que le moteur écrit sur les
     * deux. En cas d'échec, l'affichage revient au scénario enregistré et
     * l'échec se dit. En mock, rien ne s'écrit : la bascule reste locale.
     */
    async function setScenarioRetenu(
        scenario: OptimTurpeV2Scenario
    ): Promise<void> {
        scenarioErreur.value = null
        retenueSeq += 1
        const monTour = retenueSeq
        afficherScenario(scenario)
        const appelee =
            scenario === "sans" ? etudeSansCmdps.value : etudeAvecCmdps.value
        const id = appelee?._id
        if (!id || useMock.value) {
            await explainCurrent()
            return
        }
        try {
            const rendue = await service.retenirScenario(
                String(id),
                CODE_SCENARIO_RETENU[scenario]
            )
            // Confirmé par le serveur, même si un clic plus récent est en
            // cours : c'est désormais ce que le rechargement montrerait.
            scenarioEnregistre = scenario
            if (monTour !== retenueSeq) return
            if (rendue) poserEtudeRetenue(rendue, scenario)
        } catch (error) {
            if (monTour !== retenueSeq) return
            afficherScenario(scenarioEnregistre)
            scenarioErreur.value = nommerEchecGeste(
                error,
                "L'enregistrement du scénario retenu"
            )
            return
        }
        await explainCurrent()
    }

    /** Affiche la jambe d'un scénario de la paire, sans rien écrire. */
    function afficherScenario(scenario: OptimTurpeV2Scenario): void {
        scenarioRetenu.value = scenario
        const etude =
            scenario === "sans" ? etudeSansCmdps.value : etudeAvecCmdps.value
        if (etude) currentEtude.value = etude
    }

    /**
     * Pose l'étude rendue par la route scénario à la place de sa jambe. Le
     * site du courriel, servi par la seule relecture, est gardé ; la jumelle
     * porte le même `scenario_retenu` que le moteur vient d'écrire sur elle.
     */
    function poserEtudeRetenue(
        rendue: OptimTurpeV2Etude,
        scenario: OptimTurpeV2Scenario
    ): void {
        const jambe = scenario === "sans" ? etudeSansCmdps : etudeAvecCmdps
        const jumelle = scenario === "sans" ? etudeAvecCmdps : etudeSansCmdps
        const precedente = jambe.value
        const posee: OptimTurpeV2Etude = {
            ...rendue,
            site_du_courriel:
                rendue.site_du_courriel ?? precedente?.site_du_courriel,
        }
        jambe.value = posee
        if (jumelle.value && rendue.scenario_retenu)
            jumelle.value = {
                ...jumelle.value,
                scenario_retenu: rendue.scenario_retenu,
            }
        currentEtude.value = posee
        // L'historique relit la même paire : la ligne nomme le scénario retenu.
        reporterScenarioRetenu(
            [posee, jumelle.value].filter(
                (e): e is OptimTurpeV2Etude => e !== null
            )
        )
    }

    /** Mock-only : charge une fixture par id (Storybook / démo des états). */
    function setMockEtude(id: string): void {
        currentEtude.value = optimTurpeV2MockEtudes[id] ?? null
    }

    /**
     * Déclenche/rafraîchit la narration `explain` de l'étude courante (DEV-715). En mock, no-op
     * (les fixtures portent déjà leur `explain`) ; en réel, appelle la façade et remplace l'étude.
     *
     * Fail-soft (DEV-762) : la narration est un ENRICHISSEMENT optionnel. Un échec de l'explain
     * (budget, LLM, droits) ne doit JAMAIS masquer l'étude déjà calculée → on avale l'erreur ici
     * au lieu de la laisser remonter (sinon l'utilisateur voit « 500 » alors que l'optim a réussi).
     */
    async function explainCurrent(): Promise<void> {
        const etude = currentEtude.value
        const id = etude?._id
        if (!etude || !id || useMock.value) return
        // Le texte de conclusion est composé UNE fois par le moteur et stocké
        // avec l'étude (courtage#249, DEV-2173) : on le relit. La route n'est
        // appelée que pour une étude qui n'en porte pas (étude antérieure) :
        // elle coûtait 15 à 24 s et un appel au modèle à CHAQUE affichage.
        if (explainStocke(etude)) return
        narrationSeq += 1
        const monTour = narrationSeq
        // DEV-2311 : la tâche du moteur compose la conclusion, ou l'a
        // peut-être déjà fait. On ne l'attend PAS pour afficher le résultat :
        // la relecture part à côté, et la conclusion se pose dès qu'elle arrive.
        const conduite = conduiteNarration(etude)
        if (conduite === "rien") return
        if (conduite === "demander") {
            await demanderNarration(id, monTour)
            return
        }
        void relireNarration(id, monTour, conduite === "suivre_la_tache")
    }

    /** Pose l'étude narrée là où elle est affichée, si elle l'est encore. */
    function poserNarration(id: string, narree: OptimTurpeV2Etude): void {
        if (currentEtude.value?._id === id) currentEtude.value = narree
        if (etudeSansCmdps.value?._id === id) etudeSansCmdps.value = narree
        if (etudeAvecCmdps.value?._id === id) etudeAvecCmdps.value = narree
    }

    /** La route `explain`, une fois ; fail-soft (DEV-762). */
    async function demanderNarration(
        id: string,
        monTour: number
    ): Promise<void> {
        try {
            const updated = await service.explainEtude(id)
            if (updated && monTour === narrationSeq) poserNarration(id, updated)
        } catch {
            // narration indisponible → on garde l'étude telle quelle, sans bloquer l'affichage
        }
    }

    /**
     * Relit l'étude pendant que sa narration se compose (DEV-2311).
     *
     * Deux régimes, d'après `outputs.narration` servi par le moteur :
     * - « en_cours » : relecture toutes les `NARRATION_EN_COURS_INTERVALLE_MS`,
     *   jamais d'appel à la route, jusqu'à « terminee » (la conclusion est
     *   posée) ou « echouee » (un seul appel), `NARRATION_EN_COURS_PLAFOND_MS`
     *   au plus ;
     * - règle d'avant (« echouee » ou clé absente) : relecture jusqu'à
     *   `NARRATION_ATTENTE_MS`, puis un seul appel si rien n'est arrivé.
     * Une relecture qui découvre « en_cours » passe au premier régime.
     */
    async function relireNarration(
        id: string,
        monTour: number,
        suivreLaTache: boolean
    ): Promise<void> {
        const debut = Date.now()
        let suivre = suivreLaTache
        let fin =
            debut +
            (suivre ? NARRATION_EN_COURS_PLAFOND_MS : NARRATION_ATTENTE_MS)
        while (Date.now() < fin) {
            await new Promise<void>((resolve) =>
                setTimeout(
                    resolve,
                    suivre
                        ? NARRATION_EN_COURS_INTERVALLE_MS
                        : NARRATION_INTERVALLE_MS
                )
            )
            if (monTour !== narrationSeq) return
            let relue: OptimTurpeV2Etude | null | undefined = null
            try {
                relue = await service.getEtude(id)
            } catch {
                relue = null
            }
            if (monTour !== narrationSeq) return
            if (!relue) continue
            if (explainStocke(relue)) {
                poserNarration(id, relue)
                return
            }
            const statut = relue.narration?.statut
            // Terminée sans conclusion : rien à narrer, rien à demander.
            if (statut === "terminee") return
            if (statut === "echouee") {
                suivre = false
                break
            }
            if (statut === "en_cours" && !suivre) {
                suivre = true
                fin = debut + NARRATION_EN_COURS_PLAFOND_MS
            }
        }
        if (monTour !== narrationSeq) return
        // Plafond atteint pendant que le moteur compose encore : pas d'appel,
        // la conclusion sera là à la prochaine ouverture.
        if (suivre) return
        await demanderNarration(id, monTour)
    }

    /**
     * Demande le rapport PDF de l'étude courante et renvoie l'URL du document (DEV-1914).
     *
     * Le navigateur n'envoie que l'identifiant de l'étude : la composition, les libellés et
     * tous les montants viennent de courtage. C'est la même URL qui sert au téléchargement et
     * à l'aperçu, pour que le client ne puisse pas voir à l'écran autre chose que ce qu'il
     * transmettra. No-op en mock (pas de backend). Fail-soft : un échec remplit `reportError`
     * sans casser l'affichage de l'étude.
     *
     * Deux 404 très différents, et c'est le CODE qui les sépare : un 404 sans cause nommée est
     * une route absente de cet environnement (c'était le cas partout avant ce lot, constat B8)
     * et retire les boutons ; un 404 portant une cause du moteur (`optimturpe-…`) concerne
     * CETTE étude-là et se dit à l'écran, sans retirer une fonction qui marche.
     */
    async function downloadReport(): Promise<string | null> {
        const etude = currentEtude.value
        if (!etude?._id || useMock.value) return null
        const etudeId = etude._id
        isReporting.value = true
        reportError.value = null
        try {
            const url = await service.getReportPdf(etudeId)
            // Mémorisé AVEC son étude : une bascule de scénario ou un changement de compteur
            // rend `reportUrl` nul, au lieu de servir le document de l'étude précédente.
            reportParEtude.value = url ? { etudeId, url } : null
            if (!url) {
                reportError.value =
                    "Le rapport n'a pas pu être généré. Réessayez dans un instant."
            }
            return url ?? null
        } catch (error) {
            // Même lecture morte que `applyRunError` (DEV-1582) : `handleServiceCall` transforme
            // une erreur AVEC réponse en `ServiceError`, jamais l'objet axios brut — sauf
            // `axios.isCancel(error)`, relancé tel quel (voir `applyRunError`, même garde ici).
            if (axios.isCancel(error)) return null
            // DEV-2311 : étude en cours ou sans résultat (409 nommé par le
            // moteur) : un texte de client, jamais « erreur 409 ».
            const sansDocument = messageEtudeSansDocument(error)
            if (sansDocument) {
                reportError.value = sansDocument
                return null
            }
            if (error instanceof ServiceError && error.status === 404) {
                // 404 NOMMÉ par le moteur : cette étude-là n'existe pas (ou n'appartient pas
                // à la société). La fonction, elle, est bien là : on le dit sans retirer les
                // boutons, sinon un incident d'une étude ferait disparaître le rapport de
                // toutes les autres jusqu'au rechargement de la page.
                if (error.code?.startsWith(PREFIXE_CAUSE_RAPPORT)) {
                    reportError.value = resolveServiceErrorMessage(error, {
                        surPanne:
                            "Le rapport n'a pas pu être généré. Réessayez dans un instant.",
                        surAutre: () =>
                            "Cette étude n'est plus disponible. Relancez-la pour obtenir son rapport.",
                    })
                    return null
                }
                // 404 SANS cause nommée : la route n'existe pas sur cet environnement. Ce
                // n'est pas une panne à réessayer, c'est une fonction absente.
                rapportIndisponible.value = true
                reportError.value = null
                return null
            }
            reportError.value = resolveServiceErrorMessage(error, {
                surPanne:
                    "Le rapport n'a pas pu être généré dans le délai imparti, pour une raison technique. Réessayez dans un instant.",
                surAutre: (status) =>
                    `Le rapport n'a pas pu être généré. ${consigneApresRefus(status)}`,
            })
            return null
        } finally {
            isReporting.value = false
        }
    }

    function $reset(): void {
        // Préempte une relecture encore en vol (DEV-1583) : sans ça, elle repeuplerait
        // un store qu'on vient de vider.
        runSeq += 1
        narrationSeq += 1
        annulerRelecture()
        annulerSuivis()
        currentEtude.value = null
        currentSourceId.value = ""
        batiment.value = ""
        isRunning.value = false
        isRelisant.value = false
        runError.value = null
        messageAttente.value = null
        lancementAccepte.value = false
        isReporting.value = false
        reportError.value = null
        reportParEtude.value = null
        etudeSansCmdps.value = null
        etudeAvecCmdps.value = null
        scenarioRetenu.value = "avec"
        scenarioEnregistre = "avec"
        scenarioErreur.value = null
        retenueSeq += 1
        historique.value = []
        historiqueError.value = null
        historiqueTronque.value = null
        isChargementHistorique.value = false
        isSuppressionEnCours.value = false
        geste.value = null
        gesteErreur.value = null
        refus.value = null
        refusConnus.value = {}
        margesChoisies.value = {}
        periodeDemandee = null
        ouvertureEnCours = null
        // Le périmètre est une donnée de RÉFÉRENCE du moteur, pas un état d'étude :
        // le vider forcerait un aller-retour réseau à chaque changement de compteur
        // pour relire exactement la même liste. On le garde, et `perimetreDemande`
        // avec lui.
    }

    return {
        useMock,
        currentEtude,
        currentState,
        perimetre,
        chargerPerimetre,
        refusConnus,
        historique,
        historiqueError,
        isChargementHistorique,
        isSuppressionEnCours,
        historiqueTronque,
        chargerHistorique,
        ouvrirEtude,
        ouvrirLancement,
        ouvrirSource,
        sourcesEtudieesDuBatiment,
        batiment,
        entrerSurBatiment,
        derniereEtudeAilleurs,
        supprimerEtude,
        supprimerLancement,
        retablirLancement,
        rejouerLancement,
        idsAffichesRejouables,
        geste,
        gesteErreur,
        refus,
        isRunning,
        lancementAccepte,
        isRelisant,
        runError,
        messageAttente,
        isReporting,
        reportError,
        reportUrl,
        rapportIndisponible,
        currentSourceId,
        etudeSansCmdps,
        etudeAvecCmdps,
        scenarioRetenu,
        scenarioErreur,
        getArbitragePertinent,
        setCurrentSourceId,
        runBySource,
        runArbitrage,
        setScenarioRetenu,
        setMockEtude,
        explainCurrent,
        abandonnerRunEnCours,
        downloadReport,
        $reset,
    }
})
